/**
 * Choose a new password from a reset link.
 *
 * Same rule as verification: the key in the URL is a claim, not a credential
 * this screen can validate. It is submitted together with the new password, and
 * the server decides. Nothing here inspects it, and no state on this screen
 * treats its mere presence as success.
 *
 * Arriving without a key is a real case — the backend's `account_reset_password`
 * link has no key in it — so the key can also be pasted, which is what a learner
 * whose mail client mangled the link will end up doing.
 */

import { useState, type FormEvent } from 'react';
import { Button } from '../design/Button';
import { PasswordField, TextField } from '../design/Field';
import { AuthLayout } from '../design/Feedback';
import { passwordRequirements } from '../api/auth';
import { FailureSummary, SuccessPanel, useSubmission } from './formSupport';
import { useTurnstile } from './Turnstile';
import { useAuth } from './AuthProvider';
import { forgetKeyInAddressBar, type Route } from './routes';

export interface ResetPasswordProps {
  route: Extract<Route, { name: 'reset-password' }>;
  navigate: (route: Route, options?: { replace?: boolean }) => void;
}

export function ResetPassword({ route, navigate }: ResetPasswordProps) {
  const { auth } = useAuth();
  const [key, setKey] = useState(route.key ?? '');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const { busy, failure, submit } = useSubmission();
  const turnstile = useTurnstile();

  const requirements = passwordRequirements(password, '');

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    const ok = await submit(async () => {
      await auth.resetPassword(key.trim(), password, {
        turnstileToken: turnstile.token ?? undefined,
      });
      setPassword('');
      forgetKeyInAddressBar(route);
    });
    if (ok) {
      setDone(true);
    } else {
      setPassword('');
      turnstile.reset();
    }
  }

  if (done) {
    return (
      <AuthLayout heading="Password changed">
        <SuccessPanel
          title="Your password is set"
          actions={
            <Button
              variant="primary"
              size="lg"
              onClick={() => navigate({ name: 'sign-in' }, { replace: true })}
            >
              Sign in
            </Button>
          }
        >
          <p>
            Sign in with your new password. Anyone still signed in to your account on another
            device has been signed out.
          </p>
        </SuccessPanel>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      heading="Choose a new password"
      sub="Use the link we emailed you. Links can only be used once."
      footer={
        <>
          Need a new link?{' '}
          <button
            type="button"
            className="mm-auth__link"
            onClick={() => navigate({ name: 'forgot-password' })}
          >
            Request another
          </button>
        </>
      }
    >
      {/* The key field only exists when the link did not carry one. Claiming to
          handle `key` while it is hidden would suppress the banner and swallow
          an expired-link error entirely. */}
      <FailureSummary
        failure={failure}
        handledFields={route.key ? ['password'] : ['key', 'password']}
      />

      <form onSubmit={onSubmit} noValidate>
        {/* Shown only when the link did not carry one, so the ordinary path is
            a single field rather than a mysterious code box. */}
        {route.key ? null : (
          <TextField
            label="Reset code"
            name="key"
            value={key}
            autoComplete="one-time-code"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            required
            help="From the email we sent you."
            error={failure?.fields.key}
            onChange={(event) => setKey(event.target.value)}
          />
        )}

        <PasswordField
          label="New password"
          name="password"
          value={password}
          autoComplete="new-password"
          required
          requirements={requirements}
          error={failure?.fields.password}
          onChange={(event) => setPassword(event.target.value)}
        />

        <turnstile.Widget />

        <div className="mm-auth__actions">
          <Button
            type="submit"
            variant="primary"
            size="lg"
            block
            loading={busy}
            disabled={turnstile.blocking}
          >
            Set new password
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
